import { useCallback } from 'react';
import GinStepAssetDrawer from '@hellodarwin/app-features/lib/features/gin-steps/common-components/gin-step-item/form-filling/gin-step-application-asset-drawer';
import GinStepBucketAssetEditorDrawer from '@hellodarwin/app-features/lib/features/gin-steps/common-components/gin-step-item/form-filling/gin-step-bucket-asset-editor-drawer';
import StepsManagerModal from '@hellodarwin/app-features/lib/features/steps-manager/modal';
import { InitialGinStepForm } from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Button from '@hellodarwin/core/lib/components/common/button';
import Loading from '@hellodarwin/core/lib/components/loading';
import { useAppDispatch } from '../../app/app-hooks';
import {
  removeProgramDocument,
  saveProgramDocument,
  type ProgramDocumentRequirement,
} from '../../features/api/program-documents-api';
import { fetchApplicationDocuments } from '../../features/api/slices/application-documents-slice';
import { fetchGinStepDocumentRequests } from '../../features/api/slices/gin-steps-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../features/api/use-client-api';
import useGinStepDocumentSubmission from '../../hooks/use-gin-step-document-submission';
import { useProgramDocuments } from '../../hooks/use-program-documents';

export interface ProgramDocumentUploadDrawerProps {
  requirement: ProgramDocumentRequirement;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

// Mount only while open. All three surfaces use this same adapter and GIN drawers.
export const ProgramDocumentUploadDrawer = ({
  requirement,
  onClose,
  onSuccess,
}: ProgramDocumentUploadDrawerProps) => {
  const dispatch = useAppDispatch();
  const api = useNewClientApi();
  const oldApi = useClientApi();
  const { selectedLocale } = useLocale();
  const { t } = useTranslations();
  const documents = useProgramDocuments(requirement.program_id);
  const refreshDocuments = documents.refresh;
  const current = documents.data?.required_documents.find(
    ({ id }) => id === requirement.id,
  );
  const submission = useGinStepDocumentSubmission({
    programId: requirement.program_id,
    relatedAssetType: requirement.asset_type,
  });

  const refresh = useCallback(async () => {
    const results = await Promise.allSettled([
      refreshDocuments(),
      dispatch(
        fetchGinStepDocumentRequests({ api: oldApi, locale: selectedLocale }),
      ).unwrap(),
      dispatch(
        fetchApplicationDocuments({
          api,
          applicationId: requirement.program_id,
        }),
      ).unwrap(),
    ]);
    results.forEach((result) => {
      if (result.status === 'rejected') {
        console.error('Failed to refresh document projections', result.reason);
      }
    });
    try {
      await onSuccess?.();
    } catch (error) {
      console.error('Failed to refresh parent document projection', error);
    }
  }, [
    api,
    dispatch,
    refreshDocuments,
    oldApi,
    onSuccess,
    requirement.program_id,
    selectedLocale,
  ]);

  // A save may make the slot read-only while its status dialog is still open.
  const statusModal = (
    <StepsManagerModal
      open={submission.applicationStatusModal.open}
      onClose={submission.closeApplicationStatusModal}
      onConfirm={submission.applicationStatusModal.onConfirm}
      neededProperties={submission.applicationStatusModal.neededProperties}
    />
  );

  if (
    documents.status === 'failed' ||
    (documents.status === 'succeeded' && !current)
  ) {
    return (
      <>
        <div role="alert">
          <Typography>{t('document-journey|documentUnavailable')}</Typography>
        </div>
        <Button onClick={onClose}>
          {t('document-journey|documentsClose')}
        </Button>
        {statusModal}
      </>
    );
  }
  if (
    !current ||
    documents.status === 'pending' ||
    !submission.assetApplicationProps
  )
    return (
      <>
        <Loading />
        {statusModal}
      </>
    );

  const operations = {
    assets: {
      ...submission.assetApplicationProps,
      deleteApplicationAssets: async (assetIds: string[]) => {
        await removeProgramDocument(api, current, assetIds);
        return { assetIds, ginStepForm: null };
      },
    },
    submitApplicationItem: async (
      ...args: Parameters<typeof submission.handleSubmitApplicationItem>
    ) => {
      const [item, update] = args;
      const result = await saveProgramDocument(
        api,
        requirement.program_id,
        item,
        update,
      );
      return { ...InitialGinStepForm, ...result };
    },
    triggerProgramStatusDialog: submission.triggerProgramStatusDialog,
  };
  const common = {
    open: true,
    isLoading: submission.isLoading,
    title: current.document_name,
    onClose,
    itemId: current.item_id,
    formApplicationId: current.form_application_id,
    applicationItem: current.application_item,
    operations,
    relatedFilesTitle: t('document-journey|existingFilesTitle'),
    flattenRelatedFiles: true,
    onSuccess: refresh,
  };
  return (
    <>
      {current.kind === 'folder' ? (
        <GinStepBucketAssetEditorDrawer
          {...common}
          downloadAsset={submission.handleDownloadAsset}
        />
      ) : (
        <GinStepAssetDrawer
          {...common}
          modificationSuccessMessage={t(
            'document-journey|documentRenameSuccess',
          )}
          modificationErrorMessage={t('document-journey|documentRenameError')}
          removalSuccessMessage={t(
            'document-journey|documentRemoveSuccess',
          )}
          removalErrorMessage={t('document-journey|documentRemoveError')}
          removeApplicationItem={() => removeProgramDocument(api, current)}
          ginStepItemAssetType={current.asset_type}
          ginStepItemCustomAssetType={
            current.custom_asset_type ?? current.document_name
          }
          companyName={operations.assets.companyName}
          grantName={operations.assets.grantName}
        />
      )}
      {statusModal}
    </>
  );
};
